import '@fontsource-variable/geist/wght.css';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FunnelPlus } from 'lucide-react';
import { BoardSearchFiltersMenu } from './BoardSearchFiltersMenu';
import {
  BOARD_FILTER_SCOPE,
  applyPanelFiltersToScope,
  clearFiltersForScope,
  getActiveFilterCountForScope,
  getDisplayFiltersForScope,
} from './filterScopeUtils';
import { getCountsPropsForScope } from './buildBoardSearchFilterOptions';
import { computeTechnicianFilterCounts } from './computeTechnicianFilterCounts';
import { computeRetornoFilterCounts } from './computeRetornoFilterCounts';
import { BoardFiltersTour } from '../tour/BoardFiltersTour';
import {
  BoardToolbarExpandSearch,
  BoardToolbarFilterClickUpButton,
} from '../toolbar';
import './BoardSearchFilters.css';

export function BoardSearchWithFilters({
  searchTerm,
  onSearchChange,
  placeholder = 'Pesquisar ordens de serviço...',
  selectedColumnFilters,
  setSelectedColumnFilters,
  selectedTechnicianColumnFilters,
  setSelectedTechnicianColumnFilters,
  initialColumnFiltersState,
  initialTechnicianColumnFiltersState,
  citiesWithCounts,
  bairrosWithCounts,
  clientesWithCounts,
  tiposOSWithCounts,
  slasWithCounts,
  equipamentosWithCounts,
  statusWithCounts,
  tecnicoInicialWithCounts,
  defeitoDescricaoWithCounts,
  defeitoAlertWithCounts,
  defeitoClassificationWithCounts,
  defeitoSeverityWithCounts,
  technicianGroups,
  getTechnicianOrdersForCounts,
  getEmAbertoCountsForPicker,
  futuraOrders = [],
  retornoCountConfig,
  hasVisibleTechnicianColumns = true,
  useClickUpToolbarUi = false,
}) {
  const anchorRef = useRef(null);
  const hadVisibleTechnicianColumnsRef = useRef(hasVisibleTechnicianColumns);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuLevel, setMenuLevel] = useState('root');
  const [activeFilterKey, setActiveFilterKey] = useState(null);
  const [filterScope, setFilterScope] = useState(
    hasVisibleTechnicianColumns ? BOARD_FILTER_SCOPE.AMBOS : BOARD_FILTER_SCOPE.EM_ABERTO
  );
  const [floatingStyle, setFloatingStyle] = useState({ top: 0, left: 0 });
  const [filtersTourActive, setFiltersTourActive] = useState(false);

  useEffect(() => {
    if (!hasVisibleTechnicianColumns) {
      setFilterScope(BOARD_FILTER_SCOPE.EM_ABERTO);
      setActiveFilterKey((key) =>
        key === 'previsao' || key === 'osFutura' ? null : key
      );
    } else if (hadVisibleTechnicianColumnsRef.current === false) {
      setFilterScope(BOARD_FILTER_SCOPE.AMBOS);
    }
    hadVisibleTechnicianColumnsRef.current = hasVisibleTechnicianColumns;
  }, [hasVisibleTechnicianColumns]);

  const displayFilters = getDisplayFiltersForScope(
    filterScope,
    selectedColumnFilters,
    selectedTechnicianColumnFilters
  );

  const emAbertoCounts = {
    citiesWithCounts,
    bairrosWithCounts,
    clientesWithCounts,
    tiposOSWithCounts,
    slasWithCounts,
    equipamentosWithCounts,
    statusWithCounts,
    tecnicoInicialWithCounts,
    defeitoDescricaoWithCounts,
    defeitoAlertWithCounts,
    defeitoClassificationWithCounts,
    defeitoSeverityWithCounts,
  };

  const technicianCounts = React.useMemo(() => {
    if (!getTechnicianOrdersForCounts) return {};
    const orders = getTechnicianOrdersForCounts(null);
    return computeTechnicianFilterCounts(orders, technicianGroups);
  }, [getTechnicianOrdersForCounts, technicianGroups, selectedTechnicianColumnFilters]);

  const technicianPickerCounts = React.useMemo(() => {
    if (!getTechnicianOrdersForCounts) return {};
    const orders = getTechnicianOrdersForCounts(activeFilterKey || null);
    return computeTechnicianFilterCounts(orders, technicianGroups);
  }, [
    getTechnicianOrdersForCounts,
    technicianGroups,
    selectedTechnicianColumnFilters,
    activeFilterKey,
  ]);

  const emAbertoPickerCounts = React.useMemo(() => {
    if (!getEmAbertoCountsForPicker || !activeFilterKey) return emAbertoCounts;
    return getEmAbertoCountsForPicker(activeFilterKey);
  }, [getEmAbertoCountsForPicker, activeFilterKey, emAbertoCounts, selectedColumnFilters]);

  const retornoCounts = React.useMemo(() => {
    if (!getTechnicianOrdersForCounts || !retornoCountConfig) return {};
    const orders = getTechnicianOrdersForCounts('retorno');
    return computeRetornoFilterCounts(orders, retornoCountConfig);
  }, [getTechnicianOrdersForCounts, retornoCountConfig, selectedTechnicianColumnFilters]);

  const scopedCountsProps = getCountsPropsForScope(
    filterScope,
    emAbertoPickerCounts,
    technicianPickerCounts
  );

  const handleApplyPanelFilters = useCallback(
    (panelFilters) => {
      applyPanelFiltersToScope(
        panelFilters,
        filterScope,
        setSelectedColumnFilters,
        setSelectedTechnicianColumnFilters,
        selectedTechnicianColumnFilters,
        selectedColumnFilters
      );
    },
    [
      filterScope,
      selectedColumnFilters,
      selectedTechnicianColumnFilters,
      setSelectedColumnFilters,
      setSelectedTechnicianColumnFilters,
    ]
  );

  const activeFilterCount = getActiveFilterCountForScope(
    filterScope,
    selectedColumnFilters,
    selectedTechnicianColumnFilters
  );

  const updateFloatingPosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuWidth = Math.min(420, window.innerWidth - 32);
    const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
    const top = rect.bottom + 6;
    setFloatingStyle({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!menuOpen) return;
    updateFloatingPosition();
  }, [menuOpen, menuLevel, updateFloatingPosition]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onResize = () => updateFloatingPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [menuOpen, updateFloatingPosition]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handlePointerDown = (event) => {
      const t = event.target;
      if (anchorRef.current?.contains(t)) return;

      if (t.closest?.('.board-search-filters-popover')) return;

      if (t.closest?.('.equipment-consult-period-modal')) return;

      if (filtersTourActive) {
        if (
          t.closest?.('.react-joyride__overlay') ||
          t.closest?.('.react-joyride__tooltip') ||
          t.closest?.('.board-filters-tour-tooltip') ||
          t.closest?.('#react-joyride-portal') ||
          t.closest?.('.board-search-filters-floating')
        ) {
          return;
        }
        return;
      }

      if (t.closest?.('.board-search-filters-floating')) {
        setActiveFilterKey(null);
        return;
      }

      setMenuOpen(false);
      setMenuLevel('root');
      setActiveFilterKey(null);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [menuOpen, filtersTourActive]);

  const closeMenu = () => {
    if (filtersTourActive) return;
    setMenuOpen(false);
    setMenuLevel('root');
    setActiveFilterKey(null);
  };

  const handleSelectFilter = (filterKey) => {
    if (filterKey === 'clearAll') {
      clearFiltersForScope(
        filterScope,
        setSelectedColumnFilters,
        setSelectedTechnicianColumnFilters,
        initialColumnFiltersState,
        initialTechnicianColumnFiltersState
      );
      setMenuLevel('root');
      setActiveFilterKey(null);
      return;
    }
    setActiveFilterKey((prev) => (prev === filterKey ? null : filterKey));
  };

  const handlePanelClose = () => {
    setActiveFilterKey(null);
  };

  const toggleMenu = () => {
    if (menuOpen && !filtersTourActive) {
      closeMenu();
    } else {
      setMenuOpen(true);
      setMenuLevel('root');
      setActiveFilterKey(null);
    }
  };

  const ensureMenuOpenForTour = useCallback(() => {
    setMenuOpen(true);
    setMenuLevel('root');
    setActiveFilterKey(null);
  }, []);

  const floating =
    menuOpen &&
    ReactDOM.createPortal(
      <>
        <div className="board-search-filters-backdrop" aria-hidden />
        <div
          className="board-search-filters-floating"
          style={{ top: floatingStyle.top, left: floatingStyle.left }}
        >
          <BoardSearchFiltersMenu
            activeFilterKey={activeFilterKey}
            filterScope={filterScope}
            hasVisibleTechnicianColumns={hasVisibleTechnicianColumns}
            onFilterScopeChange={(scope) => {
              setFilterScope(scope);
              setActiveFilterKey(null);
            }}
            onSelectFilter={handleSelectFilter}
            onClose={closeMenu}
            onPanelClose={handlePanelClose}
            selectedColumnFilters={selectedColumnFilters}
            selectedTechnicianColumnFilters={selectedTechnicianColumnFilters}
            emAbertoCounts={emAbertoCounts}
            technicianCounts={technicianCounts}
            onApplyPanelFilters={handleApplyPanelFilters}
            displayFilters={displayFilters}
            scopedCountsProps={scopedCountsProps}
            previsaoWithCounts={technicianPickerCounts.previsaoWithCounts || []}
            futuraOrders={futuraOrders}
            retornoCounts={retornoCounts}
            panelRemountKey={`${filterScope}-${activeFilterKey || ''}`}
          />
        </div>
      </>,
      document.body
    );

  return (
    <div
      className={[
        'board-search-filters-anchor',
        useClickUpToolbarUi ? 'board-search-filters-anchor--clickup' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      ref={anchorRef}
    >
      {useClickUpToolbarUi ? (
        <div className="board-toolbar-search-filters-row">
          <BoardToolbarExpandSearch
            value={searchTerm}
            onChange={onSearchChange}
            placeholder="Pesquisar..."
          />
          <BoardToolbarFilterClickUpButton
            onClick={toggleMenu}
            isOpen={menuOpen}
            activeCount={activeFilterCount}
            tourHighlight={filtersTourActive}
          />
        </div>
      ) : (
        <div className="board-search-filters-group">
          <input
            type="text"
            className="board-search-filters-input"
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          <button
            type="button"
            className={[
              'board-search-filters-trigger',
              menuOpen ? 'is-open' : '',
              activeFilterCount > 0 ? 'has-filters' : '',
              filtersTourActive ? 'is-tour-highlight' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={toggleMenu}
            title="Filtros"
            aria-label="Abrir filtros"
            aria-expanded={menuOpen}
            data-tour="bsf-trigger"
          >
            <FunnelPlus size={13} strokeWidth={2} className="board-search-filters-trigger-icon" aria-hidden />
            <span>Filtros</span>
            {activeFilterCount > 0 ? (
              <span className="board-search-filters-badge">{activeFilterCount}</span>
            ) : null}
          </button>
        </div>
      )}
      {floating}
      <BoardFiltersTour
        menuOpen={menuOpen}
        onEnsureMenuOpen={ensureMenuOpenForTour}
        onActiveChange={setFiltersTourActive}
        onTourEnd={() => {
          setMenuOpen(false);
          setMenuLevel('root');
          setActiveFilterKey(null);
        }}
      />
    </div>
  );
}
